import type { ChangeEvent, ReactNode } from "react";
import { useRef, useCallback, useEffect } from "react";

import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import type { PopoverProps } from "@mui/material/Popover";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { HexColorPicker } from "react-colorful";
import { useTranslation } from "react-i18next";

import { brandPrimary } from "@waterly/utils-isomorphic";

export const DEFAULT_PICKER_COLOR = brandPrimary;
const PALETTE_COLORS = [
  "#ff0000",
  "#ffa500",
  "#ffff00",
  "#008000",
  "#0000ff",
  "#4b0082",
  "#ee82ee",
  "#ffffff",
  "#000000",
];
const COLOR_PICKER_WIDTH = 200;
const PALETTE_SWATCH_SIZE = 20;

type ColorModalProps = {
  open: boolean;
  color: string;
  showPalette?: boolean;
  showHexInput?: boolean;
  preview: (color: string) => ReactNode;
  onClose?: () => void;
  onChange: (color: string) => void;
  anchorRef: PopoverProps["anchorEl"];
  anchorOrigin: PopoverProps["anchorOrigin"];
  onSave: (newColor: string) => void;
  showTextInput?: boolean;
};

const ColorPicker = ({
  open,
  preview,
  onClose,
  anchorRef,
  anchorOrigin,
  onChange,
  color,
  showTextInput = true,
  showHexInput = true,
  showPalette = true,
  onSave,
}: ColorModalProps) => {
  const { t } = useTranslation(["common"]);

  const originalColorRef = useRef<string>(color ?? DEFAULT_PICKER_COLOR);
  const wasOpenRef = useRef<boolean>(false);

  const handleClose = useCallback<Required<PopoverProps>["onClose"]>((_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
  }, []);

  const handleSave = useCallback(() => {
    onSave(color);
    originalColorRef.current = color;
    if (onClose) onClose();
  }, [color, onClose, onSave]);

  const handleCancel = useCallback(() => {
    onChange(originalColorRef.current);
    if (onClose) onClose();
  }, [onChange, onClose]);

  const handleTextInputColorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handlePaletteColorClick = useCallback(
    (newColor: string) => () => {
      onChange(newColor);
    },
    [onChange],
  );

  // Capture the starting color once per open cycle so Cancel can discard unsaved edits.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      originalColorRef.current = color ?? DEFAULT_PICKER_COLOR;
    }
    wasOpenRef.current = open;
  }, [open, color]);

  return (
    <Popover open={open} anchorEl={anchorRef} anchorOrigin={anchorOrigin} onClose={handleClose}>
      <Stack spacing={2} m={2}>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {t("common:color_picker")}
        </Typography>
        {preview(color ?? "")}
        {showHexInput && (
          <Box sx={{ width: COLOR_PICKER_WIDTH }}>
            <HexColorPicker color={color} onChange={onChange} />
          </Box>
        )}
        {showPalette && (
          <Stack
            direction="row"
            sx={{ width: COLOR_PICKER_WIDTH, justifyContent: "space-between", alignItems: "center" }}
          >
            {PALETTE_COLORS.map((paletteColor) => (
              <Button
                key={paletteColor}
                variant="contained"
                onClick={handlePaletteColorClick(paletteColor)}
                sx={{
                  backgroundColor: paletteColor,
                  width: PALETTE_SWATCH_SIZE,
                  height: PALETTE_SWATCH_SIZE,
                  minWidth: PALETTE_SWATCH_SIZE,
                  minHeight: PALETTE_SWATCH_SIZE,
                  aspectRatio: "1 / 1",
                  p: 0,
                  "&:hover": {
                    backgroundColor: paletteColor,
                  },
                }}
              >
                {" "}
              </Button>
            ))}
          </Stack>
        )}
        {showTextInput && (
          <TextField value={color} onChange={handleTextInputColorChange} label={t("common:color")} />
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={handleCancel}>
            {t("common:cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} color="secondary" startIcon={<SaveIcon />}>
            {t("common:save")}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default ColorPicker;
